import { OpenImgContextProvider } from 'openimg/react'
import {
	data,
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	useLoaderData,
} from 'react-router'
import { HoneypotProvider } from 'remix-utils/honeypot/react'
import { type Route } from './+types/root.ts'
import { GeneralErrorBoundary } from './components/error-boundary.tsx'
import { EpicProgress } from './components/progress-bar.tsx'
import { useToast } from './components/toaster.tsx'
import { href as iconsHref } from './components/ui/icon.tsx'
import { EpicToaster } from './components/ui/sonner.tsx'
import { useOptionalTheme, useTheme } from './routes/resources/theme-switch.tsx'
import tailwindStyleSheetUrl from './styles/tailwind.css?url'
import { getUserId, logout } from './utils/auth.server.ts'
import { ClientHintCheck, getHints } from './utils/client-hints.tsx'
import { prisma } from './utils/db.server.ts'
import { getEnv } from './utils/env.server.ts'
import { pipeHeaders } from './utils/headers.server.ts'
import { honeypot } from './utils/honeypot.server.ts'
import { combineHeaders, getDomainUrl, getImgSrc } from './utils/misc.tsx'
import { useNonce } from './utils/nonce-provider.ts'
import { type Theme, getTheme } from './utils/theme.server.ts'
import { makeTimings, time } from './utils/timing.server.ts'
import { getToast } from './utils/toast.server.ts'

export const links: Route.LinksFunction = () => {
	return [
		// Preload svg sprite as a resource to avoid render blocking
		{ rel: 'preload', href: iconsHref, as: 'image' },
		// DNS prefetch for performance
		{ rel: 'dns-prefetch', href: 'https://js.stripe.com' },
		{ rel: 'dns-prefetch', href: 'https://fonts.googleapis.com' },
		// Google Fonts - Outfit for logo
		{ rel: 'preconnect', href: 'https://fonts.googleapis.com' },
		{
			rel: 'preconnect',
			href: 'https://fonts.gstatic.com',
			crossOrigin: 'anonymous',
		} as const,
		{
			rel: 'stylesheet',
			href: 'https://fonts.googleapis.com/css2?family=Outfit:wght@500;600;700&display=swap',
		},
		// Favicons
		{ rel: 'icon', href: '/favicons/favicon.ico', sizes: '48x48' },
		{
			rel: 'icon',
			type: 'image/png',
			href: '/favicons/favicon-32x32.png',
			sizes: '32x32',
		},
		{
			rel: 'icon',
			type: 'image/png',
			href: '/favicons/favicon-16x16.png',
			sizes: '16x16',
		},
		{
			rel: 'apple-touch-icon',
			href: '/favicons/apple-icon-180x180.png',
			sizes: '180x180',
		},
		{
			rel: 'manifest',
			href: '/manifest.json',
			crossOrigin: 'use-credentials',
		} as const, // necessary to make typescript happy
		{ rel: 'stylesheet', href: tailwindStyleSheetUrl },
	].filter(Boolean)
}

export const meta: Route.MetaFunction = ({ data }) => {
	return [
		{
			title: data
				? 'MyBillingsMethod - Privacy-First Billings Method Tracker for Mac'
				: 'Error | MyBillingsMethod',
		},
		{
			name: 'description',
			content:
				'Track the Billings Ovulation Method with complete privacy. No cloud storage, AI-powered insights, Mac-native app. Launching April 2026. Lock in £29/year beta pricing.',
		},
		{
			name: 'keywords',
			content:
				'Billings Method, fertility tracking, NFP app, natural family planning, cervical mucus, ovulation tracking, privacy, Mac app, local-first',
		},

		// Open Graph (Facebook, LinkedIn)
		{ property: 'og:type', content: 'website' },
		{ property: 'og:url', content: 'https://mybillingsmethod.com' },
		{
			property: 'og:title',
			content: 'MyBillingsMethod - Privacy-First Billings Method Tracker',
		},
		{
			property: 'og:description',
			content:
				'Track the Billings Method with AI-powered insights and true privacy. Your data never leaves your Mac. Launching April 2026.',
		},
		{
			property: 'og:image',
			content: 'https://mybillingsmethod.com/og-image.png',
		},
		{ property: 'og:image:width', content: '1200' },
		{ property: 'og:image:height', content: '630' },
		{ property: 'og:site_name', content: 'MyBillingsMethod' },
		{ property: 'og:locale', content: 'en_GB' },

		// Twitter Card
		{ name: 'twitter:card', content: 'summary_large_image' },
		{ name: 'twitter:url', content: 'https://mybillingsmethod.com' },
		{
			name: 'twitter:title',
			content: 'MyBillingsMethod - Privacy-First Billings Method Tracker',
		},
		{
			name: 'twitter:description',
			content:
				'Track the Billings Method with AI-powered insights and true privacy. Your data never leaves your Mac.',
		},
		{
			name: 'twitter:image',
			content: 'https://mybillingsmethod.com/twitter-image.png',
		},

		// Additional SEO
		{ name: 'robots', content: 'index, follow' },
		{ name: 'googlebot', content: 'index, follow' },
		{ name: 'author', content: 'MyBillingsMethod' },
		{ name: 'theme-color', content: '#9333ea' },

		// Mobile
		{ name: 'apple-mobile-web-app-capable', content: 'yes' },
		{
			name: 'apple-mobile-web-app-status-bar-style',
			content: 'black-translucent',
		},
		{ name: 'apple-mobile-web-app-title', content: 'MyBillingsMethod' },
	]
}

export async function loader({ request }: Route.LoaderArgs) {
	const timings = makeTimings('root loader')
	const userId = await time(() => getUserId(request), {
		timings,
		type: 'getUserId',
		desc: 'getUserId in root',
	})

	const user = userId
		? await time(
				() =>
					prisma.user.findUnique({
						select: {
							id: true,
							name: true,
							username: true,
							image: { select: { objectKey: true } },
							roles: {
								select: {
									name: true,
									permissions: {
										select: { entity: true, action: true, access: true },
									},
								},
							},
						},
						where: { id: userId },
					}),
				{ timings, type: 'find user', desc: 'find user in root' },
			)
		: null
	if (userId && !user) {
		console.info('something weird happened')
		// something weird happened... The user is authenticated but we can't find
		// them in the database. Maybe they were deleted? Let's log them out.
		await logout({ request, redirectTo: '/' })
	}
	const { toast, headers: toastHeaders } = await getToast(request)
	const honeyProps = await honeypot.getInputProps()

	return data(
		{
			user,
			requestInfo: {
				hints: getHints(request),
				origin: getDomainUrl(request),
				path: new URL(request.url).pathname,
				userPrefs: {
					theme: getTheme(request),
				},
			},
			ENV: getEnv(),
			toast,
			honeyProps,
		},
		{
			headers: combineHeaders(
				{ 'Server-Timing': timings.toString() },
				toastHeaders,
			),
		},
	)
}

export const headers: Route.HeadersFunction = pipeHeaders

function Document({
	children,
	nonce,
	theme = 'light',
	env = {},
}: {
	children: React.ReactNode
	nonce: string
	theme?: Theme
	env?: Record<string, string | undefined>
}) {
	const allowIndexing = ENV.ALLOW_INDEXING !== 'false'
	const isProduction = env.MODE === 'production'
	return (
		<html lang="en" className={`${theme} h-full overflow-x-hidden`}>
			<head>
				<ClientHintCheck nonce={nonce} />
				<Meta />
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width,initial-scale=1" />
				{allowIndexing ? null : (
					<meta name="robots" content="noindex, nofollow" />
				)}
				<Links />
				{/* Plausible Analytics - privacy-friendly, no cookies, GDPR compliant */}
				{isProduction ? (
					<>
						<script
							async
							nonce={nonce}
							src="https://plausible.io/js/pa-95Jr3bHx6FhuIMwZJfYCJ.js"
						/>
						<script
							nonce={nonce}
							dangerouslySetInnerHTML={{
								__html: `window.plausible=window.plausible||function(){(plausible.q=plausible.q||[]).push(arguments)},plausible.init=plausible.init||function(i){plausible.o=i||{}};plausible.init()`,
							}}
						/>
					</>
				) : null}
			</head>
			<body className="bg-background text-foreground">
				{children}
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(env)}`,
					}}
				/>
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
			</body>
		</html>
	)
}

export function Layout({ children }: { children: React.ReactNode }) {
	// if there was an error running the loader, data could be missing
	const data = useLoaderData<typeof loader | null>()
	const nonce = useNonce()
	const theme = useOptionalTheme()
	return (
		<Document nonce={nonce} theme={theme} env={data?.ENV}>
			{children}
		</Document>
	)
}

function App() {
	const data = useLoaderData<typeof loader>()
	const theme = useTheme()
	useToast(data.toast)

	return (
		<OpenImgContextProvider
			optimizerEndpoint="/resources/images"
			getSrc={getImgSrc}
		>
			<Outlet />
			<EpicToaster closeButton position="top-center" theme={theme} />
			<EpicProgress />
		</OpenImgContextProvider>
	)
}

function AppWithProviders() {
	const data = useLoaderData<typeof loader>()
	return (
		<HoneypotProvider {...data.honeyProps}>
			<App />
		</HoneypotProvider>
	)
}

export default AppWithProviders

// this is a last resort error boundary. There's not much useful information we
// can offer at this level.
export const ErrorBoundary = GeneralErrorBoundary
